import React, { useState } from "react";
import { IonButton, IonRow, IonCol, IonItem, IonInput, IonCard, IonSearchbar, IonCardTitle } from "@ionic/react";
import { formattedOption } from "../../utilities/horse/SelectOrCreateHorseUtils";
import { getHorsesByNameGSI } from "../../utilities/horse/Horse";
import { Horse } from "../../models";
import BasicModal from "../Modals/BasicModal";

interface VerifyHorseModalProps {
    currentValue?: formattedOption | null | undefined;
    handleCancel: () => void;
    handleUseMatchedHorse: (e: formattedOption) => void;
    handleCreateNewHorse: (newHorseName: string) => void;
}

const VerifyHorseModal: React.FC<VerifyHorseModalProps> = ({ currentValue, handleCancel, handleUseMatchedHorse, handleCreateNewHorse }) => {
    // Track the user's confirmed horse name, which will be used to search the DB for the horse
    const [unConfirmedHorseName, setUnConfirmedHorseName] = useState(currentValue?.horseName || "");
    const [confirmedHorseName, setConfirmedHorseName] = useState("");
    const [isNameConfirmed, setIsNameConfirmed] = useState(false);

    // Store in state all horses that match the confirmed horse name in the DB
    const [formattedMatchedHorses, setFormattedMatchedHorses] = useState<formattedOption[] | null | undefined>();
    const [filteredFormattedMatchedHorses, setFilteredFormattedMatchedHorses] = useState<formattedOption[] | null | undefined>();
    const [searchText, setSearchText] = useState("");

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    /**
     * User confirmed the horse name
     * Search the DB by this horse name (Server-Side Filtering)
     * Only get the horse fields needed (Projection)
     */
    const handleClickConfirm = async () => {
        setError("");
        if (unConfirmedHorseName === "") {
            setError("Please include a horse name.");
        } else {
            setConfirmedHorseName(unConfirmedHorseName);
            setIsNameConfirmed(true);
            setIsLoading(true);
            const queryResult = await getHorsesByNameGSI(unConfirmedHorseName, true);
            if (queryResult.isSuccess) {
                const horsesFromDB = queryResult.result;
                const formattedHorses = formatHorses(horsesFromDB);
                setFormattedMatchedHorses(formattedHorses);
                setFilteredFormattedMatchedHorses(formattedHorses);
            }
            setIsLoading(false);
        }
    };

    const formatHorses = (horses: Horse[]) => {
        let formatted: formattedOption[] = [];
        for (let i = 0; i < horses.length; i++) {
            const horse = horses[i];
            const formattedHorse: formattedOption = formatSingleHorse(horse);
            formatted.push(formattedHorse);
        }
        return formatted;
    };

    const formatSingleHorse = (horse: Horse) => {
        const value = horse.id;
        const name = horse.name;
        const nickname = horse.nickname || "";
        const horseNameLabel = (name || "error") + (nickname ? " (" + nickname + ")" : "");
        const barnName = horse.barn?.name || horse.barnName || "unknown";
        const ownerLabel = horse.ownerName || horse.owner?.name || "unknown";

        let object: formattedOption = {
            value: value,
            label: horseNameLabel + " " + barnName,
            horseName: name,
            horseNameLabel: horseNameLabel,
            barnLabel: barnName,
            ownerLabel: ownerLabel,
            horse: horse,
        };
        return object;
    };

    /**
     * Allow user to search the matched horses by horse name, barn name
     */
    const handleSearchText = (text: string) => {
        setSearchText(text);
        if (text) {
            // Filter by text
            const filtered = formattedMatchedHorses?.filter(
                (horse) =>
                    horse.horseName.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
                    (horse.barnLabel && horse.barnLabel.toLocaleLowerCase().includes(text.toLocaleLowerCase()))
            );
            setFilteredFormattedMatchedHorses(filtered);
        } else if (text === "" && formattedMatchedHorses) {
            // Result the filtered list to the list of all formattedMatchedHorses
            setFilteredFormattedMatchedHorses(formattedMatchedHorses);
        } else {
            setFilteredFormattedMatchedHorses([]);
        }
    };

    /**
     * User found the horse by name in the RSP Database
     * Send that horse info back to the parent component
     * And close the modal
     */
    const handleClickUseMatchedHorse = (formattedHorse: formattedOption) => {
        handleUseMatchedHorse(formattedHorse);
    };

    const handleClickCreateNewHorse = () => {
        handleCreateNewHorse(confirmedHorseName);
    };

    return (
        <BasicModal
            isOpen={true}
            onClose={handleCancel}
            title="Verify Horse"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: handleCancel,
                },
            ]}>
            <>
                <IonRow className="ion-justify-content-center">
                    <IonCol sizeXs="12" sizeMd="10" className="text-center">
                        <h4 className="text-info font-weight-bold">{currentValue?.horseName ? "Is this the horse's name?" : "What is the horse's name?"}</h4>
                        <IonItem color="white">
                            <IonInput
                                type="text"
                                value={unConfirmedHorseName}
                                aria-required={true}
                                onIonChange={(e) => {
                                    setUnConfirmedHorseName(e.detail.value!);
                                }}
                            />
                        </IonItem>
                        <IonButton className="mt-3" color="success" onClick={() => handleClickConfirm()}>
                            Confirm
                        </IonButton>
                    </IonCol>
                </IonRow>
                {isNameConfirmed && (
                    <>
                        <hr className="mb-0" />
                        {formattedMatchedHorses && formattedMatchedHorses.length > 0 ? (
                            // There are horses that match the confirmed horse name
                            // Use the filtered horses to display the options to the user and allow search
                            <>
                                {/* Keep the Search Bar and Match Info at the top level */}
                                <IonRow className="m-0">
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">
                                            {formattedMatchedHorses?.length || 0} Match{formattedMatchedHorses && formattedMatchedHorses?.length !== 1 && "es"} for{" "}
                                            {confirmedHorseName}
                                        </h4>
                                    </IonCol>
                                </IonRow>
                                {formattedMatchedHorses && formattedMatchedHorses?.length > 3 && (
                                    <IonRow>
                                        <IonCol className="m-0">
                                            <IonSearchbar
                                                className="m-0"
                                                value={searchText}
                                                placeholder="Find Horse by Name or Barn Name"
                                                onIonChange={(e) => handleSearchText(e.detail.value!)}
                                                color="white"></IonSearchbar>
                                        </IonCol>
                                    </IonRow>
                                )}
                                {filteredFormattedMatchedHorses && filteredFormattedMatchedHorses.length > 0 ? (
                                    <IonRow className="small-scrollable bg-light">
                                        <IonCol>
                                            {filteredFormattedMatchedHorses.map((formattedHorse, index) => (
                                                <IonRow key={index}>
                                                    <IonCol className="bg-light p-0">
                                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                                            <IonRow className="ion-align-items-space-between py-0">
                                                                <IonCol sizeXs="12" sizeMd="8" className="p-0 ion-text-left">
                                                                    <IonCardTitle>
                                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{formattedHorse.horseName}</h4>
                                                                    </IonCardTitle>
                                                                    <p className="text-white m-0">Barn: {formattedHorse.barnLabel}</p>
                                                                    {currentValue?.horse?.height && (
                                                                        <p className="text-white ion-text-wrap m-0">Height: {currentValue?.horse?.height}</p>
                                                                    )}
                                                                </IonCol>
                                                                <IonCol sizeXs="12" sizeMd="4" className="ion-text-center align-content-center p-0">
                                                                    <IonRow className="ion-justify-content-center">
                                                                        <IonCol sizeXs="8" sizeMd="12" className="p-0">
                                                                            <IonButton
                                                                                className="py-1 m-0"
                                                                                expand="block"
                                                                                color="primary"
                                                                                onClick={() => handleClickUseMatchedHorse(formattedHorse)}>
                                                                                Select
                                                                            </IonButton>
                                                                        </IonCol>
                                                                    </IonRow>
                                                                </IonCol>
                                                            </IonRow>
                                                        </IonCard>
                                                    </IonCol>
                                                </IonRow>
                                            ))}
                                        </IonCol>
                                    </IonRow>
                                ) : (
                                    <IonRow>
                                        <IonCol>
                                            <p>There are no options that match this search.</p>
                                        </IonCol>
                                    </IonRow>
                                )}
                            </>
                        ) : (
                            <IonRow>
                                <IonCol className="text-center text-danger">
                                    <p>No matches for this horse name in the RingSide Pro database.</p>
                                </IonCol>
                            </IonRow>
                        )}
                        <hr className="m-0" />

                        <IonRow className="my-0">
                            <IonCol size="12">
                                <IonRow>
                                    <IonCol>
                                        <h4 className="mt-1 mb-0 text-info font-weight-bold">Can't find the horse?</h4>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="p-0">
                                        <IonCard className="rounded-3 py-1 px-3 mx-0 my-1 bg-dark">
                                            <IonRow className="ion-align-items-space-between py-0">
                                                <IonCol sizeXs="12" sizeMd="7" className="p-0 ion-text-left">
                                                    <IonCardTitle>
                                                        <h4 className="font-weight-bold text-white text-white ion-text-wrap m-0">{confirmedHorseName}</h4>
                                                    </IonCardTitle>
                                                    <p className="text-white ion-text-wrap m-0">Barn: {currentValue?.barnLabel}</p>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="5" className="p-0 ion-text-center align-content-center">
                                                    <IonButton className="py-1 m-0" color="tertiary" onClick={() => handleClickCreateNewHorse()}>
                                                        Create New Horse
                                                    </IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            </IonCol>
                        </IonRow>
                    </>
                )}
            </>
        </BasicModal>
    );
};
export default VerifyHorseModal;
